import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ID } from 'utils/id'

export type Trigger = {
  id: ID
  payload?: string
  type: 'feedback' | 'iframe' | 'reward'
}

const TRIGGER_TYPES = ['feedback', 'iframe', 'reward'] as const satisfies readonly Trigger['type'][]

/**
 * Whether a trigger type the API sends (a plain string in the schema) is one the UI renders.
 * Triggers of other types must not be added to the store.
 */
export const isTriggerType = (type: string): type is Trigger['type'] =>
  TRIGGER_TYPES.some((known) => known === type)

interface Context {
  [key: string]: string|number|boolean
}

const triggerAdapter = createEntityAdapter<Trigger>({
  selectId: (trigger) => trigger.id,
})

export const triggerSlice = createSlice({
  initialState: triggerAdapter.getInitialState({
    context: {},
  } as {
    context: Context
  }),
  name: 'trigger',
  reducers: {
    addContext: (state, action: PayloadAction<{ key: string; value: string|number|boolean }>) => {
      state.context[action.payload.key] = action.payload.value

      return state
    },
    addTriggers: triggerAdapter.addMany,
    clearContext: (state) => {
      state.context = {}

      return state
    },
    removeTrigger: triggerAdapter.removeOne,
    setContext: (state, action: PayloadAction<Context>) => {
      state.context = action.payload ?? {}

      return state
    },
  },
})

export const {
  addContext,
  addTriggers,
  clearContext,
  removeTrigger,
  setContext,
} = triggerSlice.actions

